import { useParams, Link } from 'react-router-dom';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';
import { useState } from 'react';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));
  const [readingChapter, setReadingChapter] = useState<number | null>(null);

  if (!comic) {
    return (
      <main className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">该漫画可能已下架或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  if (readingChapter !== null) {
    const chapter = comic.chapters_list.find((ch) => ch.id === readingChapter);
    if (chapter) {
      return (
        <>
          <ScrollToTop />
          <main className="min-h-screen pt-20 pb-16 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">{comic.category}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/comic/${comic.id}`} className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">{comic.title}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium whitespace-nowrap">{chapter.title}</span>
              </nav>

              <div className="text-center mb-8">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                  {comic.title} - {chapter.title}
                </h1>
                <p className="text-xs text-foreground-500">更新于 {chapter.updatedAt}</p>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 mb-8">
                <article className="prose-custom text-sm md:text-base leading-relaxed text-foreground-800 space-y-4">
                  {chapter.content.split('...').filter(Boolean).map((para, idx) => (
                    <p key={idx}>{para.trim()}{idx < chapter.content.split('...').filter(Boolean).length - 1 ? '...' : ''}</p>
                  ))}
                  <p className="text-center text-foreground-400 pt-4">—— 下拉继续阅读 ——</p>
                </article>
              </div>

              <div className="flex items-center justify-between gap-4">
                <button
                  onClick={() => {
                    const prev = comic.chapters_list.find((ch) => ch.number === chapter.number - 1);
                    if (prev) setReadingChapter(prev.id);
                  }}
                  disabled={chapter.number <= 1}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    chapter.number <= 1
                      ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                      : 'bg-secondary-100 hover:bg-secondary-200 text-secondary-700'
                  }`}
                >
                  <i className="ri-arrow-left-line"></i>
                  上一话
                </button>
                <button
                  onClick={() => setReadingChapter(null)}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-background-100 hover:bg-background-200 text-foreground-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回目录
                </button>
                <button
                  onClick={() => {
                    const next = comic.chapters_list.find((ch) => ch.number === chapter.number + 1);
                    if (next) setReadingChapter(next.id);
                  }}
                  disabled={chapter.number >= comic.chapters_list.length}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    chapter.number >= comic.chapters_list.length
                      ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                      : 'bg-primary-500 hover:bg-primary-600 text-background-50'
                  }`}
                >
                  下一话
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </main>
        </>
      );
    }
  }

  return (
    <>
      <ScrollToTop />
      <main className="min-h-screen pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">{comic.category}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap">{comic.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="flex-shrink-0 w-full lg:w-72">
              <div className="aspect-[2/3] rounded-lg overflow-hidden sticky top-24">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`inline-block text-xs px-2.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="inline-block text-xs px-2.5 py-0.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap"
                  >
                    {comic.category}
                  </Link>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500 mb-4">
                  <span className="whitespace-nowrap">
                    <i className="ri-user-line mr-1"></i>{comic.author}
                  </span>
                  <span className="whitespace-nowrap">
                    <i className="ri-star-fill text-accent-500 mr-1"></i>{comic.rating} 分
                  </span>
                  <span className="whitespace-nowrap">
                    <i className="ri-eye-line mr-1"></i>{(comic.views / 10000).toFixed(1)}万阅读
                  </span>
                  <span className="whitespace-nowrap">
                    <i className="ri-file-list-3-line mr-1"></i>共 {comic.chapters} 话
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>
                <button
                  onClick={() => {
                    if (comic.chapters_list.length > 0) {
                      setReadingChapter(comic.chapters_list[0].id);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </button>
              </div>

              <div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  章节列表
                </h2>
                <div className="border border-background-200/70 rounded-lg overflow-hidden">
                  {comic.chapters_list.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => setReadingChapter(chapter.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm border-b border-background-100 last:border-b-0 hover:bg-primary-50/50 transition-colors duration-200 cursor-pointer text-left"
                    >
                      <span className="text-foreground-800 font-medium whitespace-nowrap">
                        {chapter.title}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {chapter.updatedAt}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {relatedComics.length > 0 && (
                <div className="mt-10">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/comic/${rc.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
                      >
                        <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden">
                          <img
                            src={rc.coverImage}
                            alt={rc.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-medium text-foreground-800 truncate">{rc.title}</h3>
                          <p className="text-xs text-foreground-500 mt-0.5">{rc.author}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}